import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { UserProfile, Movie } from '../types';
import { Hero, MovieRow, cn } from '../components/UI';
import { useLocation, Link } from 'react-router-dom';
import { Play } from 'lucide-react';
import { fetchTrending, fetchPopular, searchTMDB, fetchTopRated, fetchDiscover, getPosterSrcSet, getInitialTrendingMovies } from '../services/tmdb';
import { generateSlug } from '../utils/slug';
import { SmartFinder } from '../components/SmartFinder';
import { VibeDiscovery } from '../components/VibeDiscovery';
import { getTranslation } from '../utils/translations';

export const Home = ({ user }: { user: UserProfile | null }) => {
  const [trendingMovies, setTrendingMovies] = useState<Movie[]>(() => getInitialTrendingMovies());
  const [popularMovies, setPopularMovies] = useState<Movie[]>([]);
  const [trendingTv, setTrendingTv] = useState<Movie[]>([]);
  const [topRatedMovies, setTopRatedMovies] = useState<Movie[]>([]);
  const [anime, setAnime] = useState<Movie[]>([]);
  const [actionMovies, setActionMovies] = useState<Movie[]>([]);
  const [comedyMovies, setComedyMovies] = useState<Movie[]>([]);
  const [horrorMovies, setHorrorMovies] = useState<Movie[]>([]);
  const [sciFiMovies, setSciFiMovies] = useState<Movie[]>([]);
  const [moreMovies, setMoreMovies] = useState<Movie[]>([]);
  const [continueWatching, setContinueWatching] = useState<Movie[]>([]);
  const [searchResults, setSearchResults] = useState<Movie[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [dashboardPage, setDashboardPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasMoreDashboard, setHasMoreDashboard] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  
  const location = useLocation();
  const pathname = location.pathname;

  // Parse query parameters
  const queryParams = new URLSearchParams(location.search);
  
  // Determine typeFilter based on path or query param
  let typeFilter = queryParams.get('type') || null;
  if (pathname === '/movies') typeFilter = 'movie';
  if (pathname === '/tv') typeFilter = 'tv';

  const filterParam = queryParams.get('filter') || null;
  
  let searchQuery = queryParams.get('q') || null;
  if (pathname === '/search' && queryParams.has('q')) {
    searchQuery = queryParams.get('q');
  }

  const genreFilter = queryParams.get('genre') || null;
  let genreType = (queryParams.get('genreType') as 'movie' | 'tv') || 'movie';
  if (pathname === '/movies') genreType = 'movie';
  if (pathname === '/tv') genreType = 'tv';

  const isCustomDiscover = queryParams.get('discover') === 'true';
  const discoverType = (queryParams.get('mediaType') as 'movie' | 'tv') || 'movie';
  const discoverGenre = queryParams.get('discoverGenre') || '';
  const discoverEra = queryParams.get('era') || '';
  const discoverSort = queryParams.get('sort') || 'popularity.desc';
  const isFilterMode = !!(searchQuery || typeFilter || genreFilter || isCustomDiscover);

  useEffect(() => {
    let cancelled = false;
    let secondaryTimer: ReturnType<typeof setTimeout> | undefined;

    const loadData = async () => {
      setPage(1);
      setHasMore(true);

      if (isFilterMode) {
        setLoading(true);
        setSearchResults([]);
      }
      
      try {
        if (isCustomDiscover) {
          let params = `sort_by=${discoverSort}`;
          if (discoverGenre) {
            params += `&with_genres=${discoverGenre}`;
          }
          if (discoverEra) {
            if (discoverEra === '2020s') {
              params += discoverType === 'movie' ? '&primary_release_date.gte=2020-01-01' : '&first_air_date.gte=2020-01-01';
            } else if (discoverEra === '2010s') {
              params += discoverType === 'movie' 
                ? '&primary_release_date.gte=2010-01-01&primary_release_date.lte=2019-12-31' 
                : '&first_air_date.gte=2010-01-01&first_air_date.lte=2019-12-31';
            } else if (discoverEra === '2000s') {
              params += discoverType === 'movie' 
                ? '&primary_release_date.gte=2000-01-01&primary_release_date.lte=2009-12-31' 
                : '&first_air_date.gte=2000-01-01&first_air_date.lte=2009-12-31';
            } else if (discoverEra === '90s') {
              params += discoverType === 'movie' 
                ? '&primary_release_date.gte=1990-01-01&primary_release_date.lte=1999-12-31' 
                : '&first_air_date.gte=1990-01-01&first_air_date.lte=1999-12-31';
            }
          }
          if (discoverSort.includes('vote_average.desc')) {
            params += '&vote_count.gte=100';
          }
          const results = await fetchDiscover(discoverType, params, 1);
          if (!cancelled) {
            setSearchResults(results);
            setHasMore(results.length > 0);
          }
        } else if (searchQuery) {
          const results = await searchTMDB(searchQuery, 1);
          if (!cancelled) {
            setSearchResults(results);
            setHasMore(results.length > 0);
          }
        } else if (genreFilter) {
          const params = (genreFilter === '16' && genreType === 'tv')
            ? 'with_genres=16&with_original_language=ja' 
            : `with_genres=${genreFilter.replace(/,/g, '|')}`;
          const results = await fetchDiscover(genreType, params, 1);
          if (!cancelled) {
            setSearchResults(results);
            setHasMore(results.length > 0);
          }
        } else if (typeFilter) {
          let results: Movie[] = [];
          const type = typeFilter as 'movie' | 'tv';
          
          if (filterParam === 'trending') {
            results = await fetchTrending(type, 1);
          } else if (filterParam === 'top_rated') {
            results = await fetchTopRated(type, 1);
          } else {
            results = await fetchPopular(type, 1);
          }
          
          if (!cancelled) {
            setSearchResults(results);
            setHasMore(results.length > 0);
          }
        } else {
          // 1. Fetch Trending Movies first so Hero (LCP) and the first row render immediately
          const tMovies = await fetchTrending('movie');
          if (cancelled) return;
          if (tMovies.length > 0) {
            setTrendingMovies(tMovies);
          }
          setMoreMovies([]);
          setDashboardPage(1);
          setHasMoreDashboard(true);
          setLoading(false);

          // 2. Defer below-the-fold rows slightly so the mobile CPU & network prioritize Hero + first row
          secondaryTimer = setTimeout(() => {
            if (cancelled) return;
            Promise.all([
              fetchTrending('tv'),
              fetchTopRated('movie'),
              fetchDiscover('tv', 'with_genres=16&with_original_language=ja'),
              fetchDiscover('movie', 'with_genres=28'),
              fetchDiscover('movie', 'with_genres=35'),
              fetchDiscover('movie', 'with_genres=27'),
              fetchDiscover('movie', 'with_genres=878')
            ]).then(([tTv, trMovies, animeList, actionList, comedyList, horrorList, sciFiList]) => {
              if (cancelled) return;
              setTrendingTv(tTv);
              setTopRatedMovies(trMovies);
              setAnime(animeList);
              setActionMovies(actionList);
              setComedyMovies(comedyList);
              setHorrorMovies(horrorList);
              setSciFiMovies(sciFiList);
            }).catch(err => console.error("Error loading secondary rows:", err));
          }, 500);

          // 3. Fetch Continue Watching dynamically if user is logged in
          if (user) {
            try {
              const [{ collection, query, orderBy, getDocs, limit }, { db }] = await Promise.all([
                import('firebase/firestore'),
                import('../firebase'),
              ]);
              if (cancelled) return;
              const historyRef = collection(db, 'users', user.uid, 'history');
              const qFallback = query(historyRef, orderBy('watchedAt', 'desc'), limit(50));
              
              const historySnap = await getDocs(qFallback);
              if (cancelled) return;
              const historyList: Movie[] = [];
              historySnap.forEach(doc => {
                const data = doc.data();
                if (data.progress > 0 && data.progress < 95) {
                  historyList.push({
                    id: data.movieId,
                    title: data.title,
                    thumbnail: data.thumbnail,
                    type: data.type,
                    progress: data.progress,
                    season: data.season,
                    episode: data.episode,
                    videoUrl: '', // Not needed for row
                    description: '',
                    category: ''
                  });
                }
              });
              setContinueWatching(historyList);
            } catch (error) {
              console.error("Error fetching continue watching:", error);
            }
          }
        }
      } catch (error) {
        console.error("Error loading data:", error);
        if (!cancelled) setHasMore(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => {
      cancelled = true;
      if (secondaryTimer) clearTimeout(secondaryTimer);
    };
  }, [searchQuery, typeFilter, genreFilter, genreType, filterParam, isCustomDiscover, discoverType, discoverGenre, discoverEra, discoverSort, user?.uid, isFilterMode]);

  const loadMore = async () => {
    if (isFetchingMore || !hasMore) return;
    
    setIsFetchingMore(true);
    const nextPage = page + 1;
    let newResults: Movie[] = [];
    
    if (isCustomDiscover) {
      let params = `sort_by=${discoverSort}`;
      if (discoverGenre) {
        params += `&with_genres=${discoverGenre}`;
      }
      if (discoverEra) {
        if (discoverEra === '2020s') {
          params += discoverType === 'movie' ? '&primary_release_date.gte=2020-01-01' : '&first_air_date.gte=2020-01-01';
        } else if (discoverEra === '2010s') {
          params += discoverType === 'movie' 
            ? '&primary_release_date.gte=2010-01-01&primary_release_date.lte=2019-12-31' 
            : '&first_air_date.gte=2010-01-01&first_air_date.lte=2019-12-31';
        } else if (discoverEra === '2000s') {
          params += discoverType === 'movie' 
            ? '&primary_release_date.gte=2000-01-01&primary_release_date.lte=2009-12-31' 
            : '&first_air_date.gte=2000-01-01&first_air_date.lte=2009-12-31';
        } else if (discoverEra === '90s') {
          params += discoverType === 'movie' 
            ? '&primary_release_date.gte=1990-01-01&primary_release_date.lte=1999-12-31' 
            : '&first_air_date.gte=1990-01-01&first_air_date.lte=1999-12-31';
        }
      }
      if (discoverSort.includes('vote_average.desc')) {
        params += '&vote_count.gte=100';
      }
      newResults = await fetchDiscover(discoverType, params, nextPage);
    } else if (searchQuery) {
      newResults = await searchTMDB(searchQuery, nextPage);
    } else if (genreFilter) {
      const params = (genreFilter === '16' && genreType === 'tv')
        ? 'with_genres=16&with_original_language=ja' 
        : `with_genres=${genreFilter.replace(/,/g, '|')}`;
      newResults = await fetchDiscover(genreType, params, nextPage);
    } else if (typeFilter) {
      const type = typeFilter as 'movie' | 'tv';
      if (filterParam === 'trending') {
        newResults = await fetchTrending(type, nextPage);
      } else if (filterParam === 'top_rated') {
        newResults = await fetchTopRated(type, nextPage);
      } else {
        newResults = await fetchPopular(type, nextPage);
      }
    }
    
    if (newResults.length > 0) {
      setSearchResults(prev => [...prev, ...newResults]);
      setPage(nextPage);
    } else {
      setHasMore(false);
    }
    setIsFetchingMore(false);
  };

  const loadMoreDashboard = async () => {
    if (isFetchingMore || !hasMoreDashboard) return;
    
    setIsFetchingMore(true);
    const nextPage = dashboardPage + 1;
    const newResults = await fetchDiscover('movie', 'sort_by=popularity.desc', nextPage);
    
    if (newResults.length > 0) {
      setMoreMovies(prev => [...prev, ...newResults]);
      setDashboardPage(nextPage);
    } else {
      setHasMoreDashboard(false);
    }
    setIsFetchingMore(false);
  };

  const displayMovies = useMemo(() => {
    if (searchQuery || typeFilter || genreFilter || isCustomDiscover) return searchResults;
    return trendingMovies;
  }, [searchQuery, typeFilter, genreFilter, isCustomDiscover, searchResults, trendingMovies]);

  const featuredMovies = useMemo(() => {
    return displayMovies.slice(0, 10);
  }, [displayMovies]);

  const isSearchOrFilter = !!(searchQuery || genreFilter || isCustomDiscover || filterParam || queryParams.get('type'));
  const canonicalUrl = `https://cinestream.ca${pathname === '/' ? '/' : pathname}`;

  if (loading && isFilterMode) {
    return (
      <div className="min-h-screen bg-transparent flex flex-col items-center justify-center text-white space-y-6">
        <Helmet>
          <title>CineStream | Free Movies to Watch Online HD - No Ads, No Sign Up</title>
          <link rel="canonical" href={canonicalUrl} />
          <meta name="robots" content={isSearchOrFilter ? "noindex, follow" : "index, follow"} />
        </Helmet>
        <div className="relative flex items-center justify-center">
          {/* Pulsing neon red background ring */}
          <div className="absolute w-20 h-20 rounded-full bg-red-600/30 blur-xl animate-pulse" />
          {/* Primary circular spinner */}
          <div className="w-16 h-16 border-4 border-zinc-800 border-t-red-600 rounded-full animate-spin relative z-10" />
        </div>
        <div className="text-center space-y-1.5 relative z-10">
          <h1 className="text-xl font-black tracking-widest uppercase text-white animate-pulse">
            Cine<span className="text-red-500">Stream</span>
          </h1>
          <p className="text-xs text-zinc-300 font-bold tracking-wider font-mono uppercase">
            Loading Catalog Content...
          </p>
        </div>
        
        {/* Skeleton simulation row to prevent sudden layout shifts */}
        <div className="absolute bottom-12 w-full max-w-4xl px-8 hidden md:grid grid-cols-5 gap-6 opacity-20">
          {[1, 2, 3, 4, 5].map((idx) => (
            <div key={idx} className="aspect-[2/3] bg-zinc-800 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent pb-20 relative overflow-hidden">
      {/* Vibrant Ambient Background Glows (desktop only to avoid mobile GPU blur lag) */}
      <div className="hidden md:block fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-red-900/20 blur-[120px] mix-blend-screen" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[60%] rounded-full bg-rose-900/10 blur-[120px] mix-blend-screen" />
        <div className="absolute -bottom-[20%] left-[20%] w-[60%] h-[50%] rounded-full bg-orange-900/10 blur-[150px] mix-blend-screen" />
      </div>

      <Helmet>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="robots" content={isSearchOrFilter ? "noindex, follow" : "index, follow"} />
        <title>CineStream | Free Movies to Watch Online HD - No Ads, No Sign Up</title>
        <meta name="description" content="Watch thousands of free movies online HD and trending TV shows on CineStream. Enjoy fast streaming, zero ad popups, no registration, and multi-server backup links." />
        <meta name="keywords" content="free movies to watch, cinestream, free movies online HD, free movies online no ads, watch free movies online, free tv shows online, watch series online, HD streaming, online movie index, watch anime online" />
        <meta name="author" content="CineStream Entertainment Index" />
        <meta name="rating" content="General" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content="CineStream - Watch Free Movies and TV Shows Online in HD" />
        <meta property="og:description" content="Stream the latest trending blockbuster movies, popular TV series, and hit anime series in 1080p HD. Zero ads, zero registrations, unlimited playback." />
        <meta property="og:image" content="https://placehold.co/1200x630/000000/e11d48?text=CineStream+Free+HD+Movies" />
        <meta property="og:site_name" content="CineStream" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CineStream - Watch Free Movies Online in HD" />
        <meta name="twitter:description" content="Watch trending free movies and TV shows online on CineStream with zero ads, high speed playback, and no registrations." />
        <meta name="twitter:image" content="https://placehold.co/1200x630/000000/e11d48?text=CineStream+Free+HD+Movies" />

        {/* JSON-LD Structured Data for Search Engine Optimization */}
        <script type="application/ld+json">
          {JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "name": "CineStream",
              "url": "https://cinestream.ca/",
              "description": "CineStream is a free movie and TV show streaming index providing high-speed 1080p streams with zero ads and no sign-up.",
              "potentialAction": {
                "@type": "SearchAction",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": "https://cinestream.ca/search?q={search_term_string}"
                },
                "query-input": "required name=search_term_string"
              }
            },
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "CineStream",
              "url": "https://cinestream.ca/",
              "logo": "https://placehold.co/512/e11d48/ffffff?text=CineStream"
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "Is CineStream free to watch movies and TV shows?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, CineStream is 100% free with no subscription fees, no credit card requirements, and no mandatory registration. You can start streaming immediately."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How does CineStream ensure high-speed video playback?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "CineStream utilizes dynamic multi-server aggregation, active speed ping testing, and failover mirrors (including AutoEmbed, VidSrc, VIP, and GDrive) to deliver seamless HD video playback."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Do I need to turn off my ad blocker or register an account?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "No. CineStream is designed to be fully ad-free with zero popups or redirects. Registration is entirely optional if you wish to track your continue-watching history across devices."
                  }
                }
              ]
            }
          ])}
        </script>
      </Helmet>
      
      {/* Screen-reader H1 for structural completeness & SEO index ranking */}
      <h1 className="sr-only">CineStream - Watch Free Movies & TV Shows Online in HD</h1>

      {isFilterMode && !loading && displayMovies.length === 0 ? (
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-white space-y-6 px-4 text-center">
          <h2 className="text-4xl font-bold text-red-500">No Results Found</h2>
          <p className="text-gray-300 max-w-md">
            We couldn't find any movies or TV shows matching your criteria.
          </p>
        </div>
      ) : (
        <>
          {!isFilterMode && <Hero movies={featuredMovies} />}
          
          <div className={cn(
            "relative z-10 pb-12",
            (!searchQuery && !typeFilter && !genreFilter && !isCustomDiscover) ? "pt-4" : "pt-24"
          )}>
            <SmartFinder />

            {searchQuery || typeFilter || genreFilter || isCustomDiscover ? (
              <div className="px-4 md:px-12">
                <h2 className="text-2xl font-bold text-white mb-6">
                  {isCustomDiscover ? (
                    `Smart Filter: ${discoverType === 'movie' ? 'Movies' : 'TV Shows'}` +
                    `${discoverGenre ? ` • ${discoverGenre === '28' ? 'Action & Adventure' : discoverGenre === '35' ? 'Comedy' : discoverGenre === '27' ? 'Horror' : discoverGenre === '878' ? 'Sci-Fi & Fantasy' : discoverGenre === '16' ? 'Anime / Animation' : discoverGenre === '10749' ? 'Romance' : discoverGenre === '9648' ? 'Mystery' : 'Genre'}` : ''}` +
                    `${discoverEra ? ` • ${discoverEra}` : ''}`
                  ) : searchQuery ? `Search results for "${searchQuery}"` : 
                   genreFilter ? (
                     genreFilter.includes('16') ? 'Popular Anime' :
                     genreFilter.includes('28') ? 'Action Movies' :
                     genreFilter.includes('35') ? 'Comedy Movies' :
                     genreFilter.includes('27') ? 'Horror Movies' :
                     genreFilter.includes('878') ? 'Sci-Fi & Fantasy' : 'Explore Genre'
                   ) :
                   typeFilter === 'movie' ? (
                     filterParam === 'trending' ? 'Trending Movies' :
                     filterParam === 'top_rated' ? 'Top Rated Movies' : 'Popular Movies'
                   ) : (
                     filterParam === 'top_rated' ? 'Top Rated TV Shows' : 'Trending TV Shows'
                   )}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
                  {displayMovies.map(movie => (
                    <Link 
                      key={movie.id} 
                      to={`${generateSlug(movie)}?type=${movie.type}`}
                      style={{ contentVisibility: 'auto', containIntrinsicSize: '200px 300px' }}
                      className="aspect-[2/3] relative group cursor-pointer overflow-hidden rounded-lg shadow-lg hover:shadow-[0_0_30px_rgba(220,38,38,0.4)] transition-all duration-500 hover:scale-[1.08] border border-transparent hover:border-red-600/30 hover:z-40 bg-zinc-900"
                    >
                      <img 
                        src={movie.thumbnail || `https://placehold.co/342x513/000000/e11d48?text=${encodeURIComponent(movie.title)}`} 
                        srcSet={getPosterSrcSet(movie.thumbnail)}
                        sizes="(max-width: 640px) 45vw, (max-width: 1024px) 22vw, 200px"
                        alt={movie.title}
                        width={342}
                        height={513}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        referrerPolicy="no-referrer"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center shadow-2xl scale-75 group-hover:scale-100 transition-transform duration-300">
                          <Play className="w-6 h-6 fill-white text-white" />
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black via-black/80 to-transparent translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <p className="text-white text-xs font-black truncate drop-shadow-md">{movie.title}</p>
                        <p className="text-[10px] text-gray-300 font-bold">{movie.year} • {movie.type === 'tv' ? 'TV Show' : 'Movie'}</p>
                      </div>
                    </Link>
                  ))}
                </div>
                
                {hasMore && (
                  <div className="mt-12 flex justify-center">
                    <button
                      type="button"
                      onClick={loadMore}
                      disabled={isFetchingMore}
                      className="px-8 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-900/50 disabled:cursor-not-allowed text-white font-bold rounded-full transition-all duration-300 shadow-lg hover:shadow-red-600/40 flex items-center gap-2"
                    >
                      {isFetchingMore ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Load More'
                      )}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {continueWatching.length > 0 && (
                  <MovieRow title={getTranslation('continueWatching')} movies={continueWatching} link="/history" priority />
                )}
                <MovieRow title={getTranslation('trendingMovies')} movies={trendingMovies} link="/?type=movie&filter=trending" priority />
                
                {/* Curated Movie Matcher */}
                <VibeDiscovery />

                {topRatedMovies.length > 0 && (
                  <MovieRow title={getTranslation('topRatedMovies')} movies={topRatedMovies} link="/?type=movie&filter=top_rated" />
                )}
                {trendingTv.length > 0 && (
                  <MovieRow title={getTranslation('trendingTv')} movies={trendingTv} link="/?type=tv&filter=trending" />
                )}
                {anime.length > 0 && (
                  <MovieRow title={getTranslation('popularAnime')} movies={anime} link="/?genre=16&genreType=tv" />
                )}
                {actionMovies.length > 0 && (
                  <MovieRow title={getTranslation('actionMovies')} movies={actionMovies} link="/?genre=28&genreType=movie" />
                )}
                {comedyMovies.length > 0 && (
                  <MovieRow title={getTranslation('comedyMovies')} movies={comedyMovies} link="/?genre=35&genreType=movie" />
                )}
                {sciFiMovies.length > 0 && (
                  <MovieRow title={getTranslation('scifiFantasy')} movies={sciFiMovies} link="/?genre=878|14&genreType=movie" />
                )}
                {horrorMovies.length > 0 && (
                  <MovieRow title={getTranslation('horrorMovies')} movies={horrorMovies} link="/?genre=27&genreType=movie" />
                )}
                
                {moreMovies.length > 0 && (
                  <div className="px-4 md:px-12 mt-12">
                    <h2 className="text-2xl font-bold text-white mb-6">{getTranslation('moreToExplore')}</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
                      {moreMovies.map(movie => (
                        <Link 
                          key={movie.id} 
                          to={`${generateSlug(movie)}?type=${movie.type}`}
                          style={{ contentVisibility: 'auto', containIntrinsicSize: '200px 300px' }}
                          className="aspect-[2/3] relative group cursor-pointer overflow-hidden rounded-lg shadow-lg hover:shadow-red-600/20 transition-all duration-300 hover:scale-105 bg-zinc-900"
                        >
                          <img 
                            src={movie.thumbnail || `https://placehold.co/342x513/000000/e11d48?text=${encodeURIComponent(movie.title)}`} 
                            srcSet={getPosterSrcSet(movie.thumbnail)}
                            sizes="(max-width: 640px) 45vw, (max-width: 1024px) 22vw, 200px"
                            alt={movie.title}
                            width={342}
                            height={513}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                            referrerPolicy="no-referrer"
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center shadow-2xl scale-75 group-hover:scale-100 transition-transform duration-300">
                              <Play className="w-6 h-6 fill-white text-white" />
                            </div>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black via-black/80 to-transparent translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                            <p className="text-white text-xs font-black truncate drop-shadow-md">{movie.title}</p>
                            <p className="text-[10px] text-gray-300 font-bold">{movie.year} • {movie.type === 'tv' ? 'TV Show' : 'Movie'}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                
                {hasMoreDashboard && (
                  <div className="mt-12 flex justify-center">
                    <button
                      type="button"
                      onClick={loadMoreDashboard}
                      disabled={isFetchingMore}
                      className="px-8 py-3 bg-zinc-900 hover:bg-zinc-800 disabled:bg-zinc-950 disabled:cursor-not-allowed text-white font-bold rounded-full transition-all duration-300 border border-white/15 hover:border-red-500/50 flex items-center gap-2"
                    >
                      {isFetchingMore ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Loading...
                        </>
                      ) : (
                        getTranslation('exploreMore')
                      )}
                    </button>
                  </div>
                )}

                {/* Rich SEO Editorial & Streaming Index Guide */}
                <section className="mt-20 px-4 md:px-12 pt-12 border-t border-white/10 text-zinc-200 space-y-12">
                  <div className="max-w-5xl mx-auto space-y-6 text-center md:text-left">
                    <span className="text-xs uppercase tracking-widest text-red-300 font-extrabold font-mono bg-red-600/20 px-3 py-1 rounded-full border border-red-400/30">
                      Free HD Movie & TV Stream Index
                    </span>
                    <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight leading-tight">
                      Watch Free Movies Online & Popular TV Shows on <span className="text-red-500">CineStream</span>
                    </h2>
                    <p className="text-zinc-300 text-sm sm:text-base leading-relaxed">
                      Welcome to <strong>CineStream</strong>, the web's leading high-definition streaming index built for movie enthusiasts and TV show bingers alike. Our automated web crawler scans multi-provider streaming mirrors in real time to index working 1080p and 720p stream links, ensuring you get instant playback without ad popups, forced downloads, or paid subscriptions.
                    </p>
                  </div>

                  {/* Core Feature Grid for Search Engine Indexing */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                    <div className="bg-zinc-900/50 border border-white/10 p-6 rounded-2xl space-y-3">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500" /> Zero Ads & No Registration
                      </h3>
                      <p className="text-xs text-zinc-300 leading-relaxed">
                        Enjoy uncompromised cinematic entertainment without annoying redirects, commercial breaks, or account registration forms. Just search and watch immediately.
                      </p>
                    </div>

                    <div className="bg-zinc-900/50 border border-white/10 p-6 rounded-2xl space-y-3">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500" /> Multi-Server Mirror Failover
                      </h3>
                      <p className="text-xs text-zinc-300 leading-relaxed">
                        If one stream server buffers or goes offline, CineStream automatically tests and switches between AutoEmbed, VidSrc, VIP, and GDrive backends seamlessly.
                      </p>
                    </div>

                    <div className="bg-zinc-900/50 border border-white/10 p-6 rounded-2xl space-y-3">
                      <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500" /> Daily Updated HD Catalog
                      </h3>
                      <p className="text-xs text-zinc-300 leading-relaxed">
                        From trending Hollywood box-office hits and critically acclaimed indies to Japanese anime series and TV dramas, our index is updated hourly.
                      </p>
                    </div>
                  </div>

                  {/* Comprehensive Streaming FAQs Section */}
                  <div className="max-w-5xl mx-auto bg-zinc-950/60 border border-white/10 p-6 sm:p-10 rounded-3xl space-y-8">
                    <div className="border-b border-white/10 pb-4">
                      <h2 className="text-2xl font-extrabold text-white">Frequently Asked Questions (FAQ)</h2>
                      <p className="text-xs text-zinc-300 mt-1">Everything you need to know about streaming on CineStream</p>
                    </div>

                    <div className="space-y-6 text-sm">
                      <div className="space-y-2">
                        <h3 className="text-white font-bold text-base">Q: Is CineStream completely free to use?</h3>
                        <p className="text-zinc-300 leading-relaxed">
                          Yes, CineStream is 100% free forever. We do not require credit card details, monthly memberships, or personal registration. You can browse, search, and watch unlimited movies and TV episodes for free.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-white font-bold text-base">Q: How do I fix video buffering or stream loading errors?</h3>
                        <p className="text-zinc-300 leading-relaxed">
                          If a video takes long to load or experiences buffering, click the <strong>"Server"</strong> dropdown located directly beneath the video player on the Watch page. Select an alternative server like <em>AutoEmbed</em> or <em>VidSrc VIP</em>. Our real-time speed test indicator shows current server latency.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-white font-bold text-base">Q: Can I watch movies on my smartphone, tablet, or Smart TV?</h3>
                        <p className="text-zinc-300 leading-relaxed">
                          Absolutely. CineStream is engineered with a mobile-first responsive layout and standard HTML5 video elements that support full-screen mode on iOS, Android, macOS, Windows, Linux, Fire TV, Chromecast, and webOS Smart TVs.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <h3 className="text-white font-bold text-base">Q: How does CineStream protect user privacy?</h3>
                        <p className="text-zinc-300 leading-relaxed">
                          We do not track IP addresses or sell user analytics. Your watch history and preferences are securely cached locally inside your browser's encrypted storage (`localStorage`).
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};
